import type { PlayerId } from '../types';
import type { Letter } from './Letter';
import type { PlacedWord } from './PlacedWord';
import { MAX_HAND_SIZE } from '../constants/gameConstants';

export interface Player {
  id: PlayerId; // 1 or 2
  name: string; // Display name
  hand: Letter[]; // Up to 10 letters
  score: number; // Total score (sum of all placed word scores)
  wordsPlayed: PlacedWord[]; // History
  isAI: boolean; // True for computer opponent (Phase 3)
}

/**
 * Create a new player
 */
export function createPlayer(id: PlayerId, name: string, isAI: boolean = false): Player {
  return {
    id,
    name,
    hand: [],
    score: 0,
    wordsPlayed: [],
    isAI,
  };
}

/**
 * Add letters to player's hand (respecting 10-letter max)
 */
export function addLettersToHand(player: Player, letters: Letter[]): Player {
  const availableSpace = MAX_HAND_SIZE - player.hand.length;
  const lettersToAdd = letters.slice(0, availableSpace);
  
  return {
    ...player,
    hand: [...player.hand, ...lettersToAdd],
  };
}

/**
 * Remove letters from player's hand
 */
export function removeLettersFromHand(player: Player, letterIds: string[]): Player {
  const letterIdSet = new Set(letterIds);
  return {
    ...player,
    hand: player.hand.filter(letter => !letterIdSet.has(letter.id)),
  };
}

/**
 * Add a played word to player's history and update score
 */
export function addWordPlayed(player: Player, word: PlacedWord): Player {
  return {
    ...player,
    score: player.score + word.score,
    wordsPlayed: [...player.wordsPlayed, word],
  };
}

/**
 * Get available space in player's hand
 */
export function getAvailableHandSpace(player: Player): number {
  return MAX_HAND_SIZE - player.hand.length;
}
